import {memo, useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import Context from '@s-ui/react-context'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDownMedium'
import IconChevronUp from '@adv-mt/mt-svg-icons/lib/ChevronUpMedium'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {Icon, ICON_SIZES} from '@adv-mt/ui/icon'
import {Body1, Headline1} from '@adv-mt/ui/typography'

import {BASE_CLASS} from './config.js'
import useExpandableDescription from './useExpandableDescription.js'

const TEXT_ID = `${BASE_CLASS}-text`
const TITLE_ID = `description-section-title`
const BUTTON_ID = `description-toggle`

const DescriptionPanel = ({description}) => {
  const {domain, i18n} = useContext(Context)
  const {track: analyticsTrack} = useAnalyticsTrack()

  const config = domain.get('config')
  const descriptionSectionId = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS.AD_DETAIL_PAGE_SECTION_IDS.DESCRIPTION
  const {HIT_INFORMATIONS, TRACKING_EVENT_NAMES} = config.get('tracking')

  const {contentRef, isClamped, isExpanded, isTruncated, maxHeight, sectionRef, toggle} = useExpandableDescription()

  if (!description) return null

  const ChevronIcon = isExpanded ? IconChevronUp : IconChevronDown

  const trackShowDescription = async () => {
    const eventData = await domain.get('get_event_tracking_data_use_case').execute({
      event: TRACKING_EVENT_NAMES.AD_DETAIL_BUTTON_CLICKED,
      hitInformation: HIT_INFORMATIONS.AD_DETAIL_SHOW_DESCRIPTION
    })

    analyticsTrack(eventData)
  }

  const handleToggle = () => {
    if (!isExpanded) trackShowDescription()
    toggle()
  }

  return (
    <section id={descriptionSectionId} ref={sectionRef} className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-content`}>
        <Headline1 id={TITLE_ID} className={`${BASE_CLASS}-title`}>
          {i18n.t('COMMON.DETAIL_AD_DESCRIPTION')}
        </Headline1>
        <div
          id={TEXT_ID}
          style={maxHeight === null ? undefined : {maxHeight: `${maxHeight}px`}}
          className={cx(`${BASE_CLASS}-text`, {[`${BASE_CLASS}-text--expanded`]: isExpanded})}
        >
          <Body1
            ref={contentRef}
            className={cx(`${BASE_CLASS}-textContent`, {[`${BASE_CLASS}-textContent--clamped`]: isClamped})}
          >
            {htmlStringToReactElement(description)}
          </Body1>
        </div>
      </div>

      {isTruncated && (
        <Button
          id={BUTTON_ID}
          aria-labelledby={`${TITLE_ID} ${BUTTON_ID}`}
          aria-controls={TEXT_ID}
          aria-expanded={isExpanded}
          onClick={handleToggle}
          variant={BUTTON_VARIANTS.LINK}
          shape={BUTTON_SHAPES.ROUNDED}
          className={`${BASE_CLASS}-toggle`}
        >
          {isExpanded ? i18n.t('COMMON_SHOW_LESS') : i18n.t('COMMON_SHOW_MORE')}
          <Icon size={ICON_SIZES.MD}>
            <ChevronIcon size={ICON_SIZES.MD} />
          </Icon>
        </Button>
      )}
    </section>
  )
}

DescriptionPanel.displayName = 'DescriptionPanel'
DescriptionPanel.propTypes = {
  description: PropTypes.string
}

export default memo(DescriptionPanel)
